import { useAffinityCategoryLinks } from 'utils/hooks/api';
import RelatedTermsDrawerSection from 'components/DataVis/RelatedCategoriesTable/RelatedTermsDrawerSection';
import { useEndDate, useFetchCountries } from 'utils/hooks';
import useInstagramWeeks from 'utils/hooks/useInstagramWeeks';
import CategoryDescription from 'components/CategoryDescription';
import DimensionIcon from '../../DimensionIcon';
import DrawerHeader from '../../DrawerHeader';
import { FiExternalLink } from 'react-icons/fi';
import { DATA_SOURCES, INSTAGRAM_COUNTRIES } from '@/config/constants';
import InstagramTopPostsDrawerRelatedAggregate from 'components/Posts/Instagram/InstagramTopPostsDrawerRelatedAggregate';
import Line from 'components/Line';
import PillTabs from 'components/PillTabs';
import { Button } from '@/ui/design-system/components';
import { LinkTracked } from 'components/Link';
import MetricStat from 'components/OverviewCard/MetricStat';
import { POPULARITY_METADATA_BY_KEY } from 'utils/metrics';
import Stack from '../../Stack';
import ToggleWatch from 'components/Button/ToggleWatch';
import { TopKeywordsDrawerSection } from 'components/TopKeywordsSection';
import TopPostsDrawerRelatedEntities from 'components/Posts/TopPostsDrawerRelatedEntities';
import { buildExportFilename } from 'utils/formatCSV';
import { getPathFromNode } from 'utils/helpers';
import styled from 'styled-components';
import { useState } from 'react';
import { v5 as uuidv5 } from 'uuid';

const singularByPaginateBy = {
    brands: 'brand',
    categories: 'category',
};

const SOURCES = {
    GOOGLE: 'google',
    TIKTOK: 'tiktok',
    INSTAGRAM: 'instagram',
};

function TopRelatedSearchKeywords({
    countryId,
    country,
    name,
    parentEntity,
    parentEntityId,
    relatedType,
    selectedId,
}) {
    const date = useEndDate('google');
    const linkedCategoriesEnabled =
        !!(selectedId && countryId) && relatedType === 'category';
    const linkedCategoriesQuery = useAffinityCategoryLinks({
        categories: selectedId ? [selectedId] : null,
        countries: countryId ? [countryId] : null,
        enabled: linkedCategoriesEnabled,
    });
    const keywordsData = {};
    if (countryId) {
        keywordsData.countries = [countryId];
    }
    if (relatedType === 'brand') {
        keywordsData.brands = [selectedId];
    }
    if (parentEntity?.type === 'category') {
        keywordsData.categories = [parentEntityId];
    } else if (parentEntity?.type === 'brand') {
        keywordsData.brands = [parentEntityId];
    }
    if (linkedCategoriesQuery.data && relatedType === 'category') {
        keywordsData.affinities = linkedCategoriesQuery.data.map(
            ({ affinity_id }) => affinity_id,
        );
    }
    const enabled = !!(
        countryId &&
        selectedId &&
        parentEntityId &&
        (relatedType === 'brand' || keywordsData.affinities)
    );
    return (
        <TopKeywordsDrawerSection
            enabled={enabled}
            data={keywordsData}
            paginateBy={relatedType === 'brand' ? 'brands' : 'affinities'}
            name={name}
            filename={buildExportFilename({
                entity: `${parentEntity?.name} ${name}`,
                country,
                source: 'google',
                type: 'search-query',
                kind: 'data',
                date,
            })}
            displayQty={5}
            showIcon={false}
            {...(!enabled &&
                linkedCategoriesQuery.isLoading && { isLoading: true })}
        />
    );
}

function TopRelatedTikTokTerms({
    countryId,
    country,
    name,
    parentEntity,
    parentEntityId,
    relatedType,
    selectedId,
    week,
    year,
}) {
    const date = useEndDate('tiktok');
    const baseFilename = buildExportFilename({
        entity: `${parentEntity?.name} ${name}`,
        country,
        source: 'tiktok',
        type: 'related',
        kind: 'data',
        date,
    });
    const sharedProps = {
        entity: relatedType,
        week,
        year,
        countries: countryId ? [countryId] : null,
        categories: parentEntity?.type === 'category' ? [parentEntityId] : null,
        brands: parentEntity?.type === 'brand' ? [parentEntityId] : null,
        relatedCategories: relatedType === 'category' ? [selectedId] : null,
        relatedBrands: relatedType === 'brand' ? [selectedId] : null,
        name: `${parentEntity?.name || ''} ${name}`,
        relatedEntityName: name,
    };
    return (
        <RelatedTermsDrawerSection
            source={DATA_SOURCES.TIKTOK}
            showIcon
            {...sharedProps}
            filename={`${baseFilename} keywords and hashtags`}
        />
    );
}

// IG mirror — resolves IG week/year separately because the parent Pop Index
// drawer flows TT weeks down.
function TopRelatedInstagramTerms({
    countryId,
    country,
    parentEntity,
    parentEntityId,
    relatedType,
    selectedId,
    name,
}) {
    const date = useEndDate('instagram');
    const { week, year } = useInstagramWeeks();
    const baseFilename = buildExportFilename({
        entity: `${parentEntity?.name} ${name}`,
        country,
        source: 'instagram',
        type: 'related',
        kind: 'data',
        date,
    });
    const sharedProps = {
        entity: relatedType,
        week,
        year,
        countries: countryId ? [countryId] : null,
        categories: parentEntity?.type === 'category' ? [parentEntityId] : null,
        brands: parentEntity?.type === 'brand' ? [parentEntityId] : null,
        relatedCategories: relatedType === 'category' ? [selectedId] : null,
        relatedBrands: relatedType === 'brand' ? [selectedId] : null,
        name: `${parentEntity?.name || ''} ${name}`,
        relatedEntityName: name,
    };
    return (
        <RelatedTermsDrawerSection
            source={DATA_SOURCES.INSTAGRAM}
            showIcon
            {...sharedProps}
            filename={`${baseFilename} keywords and hashtags`}
        />
    );
}

const PaddedStack = styled(Stack)`
    padding: 0 1rem;
`;

const Message = styled.div`
    color: ${({ theme }) => theme.gray_2};
    font-weight: 500;
    min-height: 4.125rem;
    display: flex;
    justify-content: center;
    align-items: center;
`;

function PopularityIndexRelatedEntitiesDrawer({
    selectedId,
    selectedRow,
    paginateBy,
    countryId,
    parentEntity,
    parentEntityId,
    week,
    year,
    showAddToWatchlist = true,
}) {
    const countriesQuery = useFetchCountries();
    const country = countriesQuery.data?.find(({ id }) => id === countryId);
    const name = selectedRow?.name;
    const relatedType = singularByPaginateBy[paginateBy];
    const relatedItemTo = getPathFromNode({
        type: relatedType,
        id: selectedId,
        name,
        depth: relatedType === 'category' ? 'cat5' : undefined,
    });
    let watchlistData = {};
    if (parentEntity?.type === 'category') {
        watchlistData = {
            name,
            type: 'related-category',
            categoryId: parentEntityId,
            relatedCategoryId: selectedId,
        };
    } else if (parentEntity?.type === 'brand') {
        watchlistData = {
            name,
            type: 'related-category',
            brandId: parentEntityId,
            relatedCategoryId: selectedId,
        };
    }

    const showInstagram = INSTAGRAM_COUNTRIES.includes(country?.text_id);
    const sourceTabs = [
        { value: SOURCES.GOOGLE, label: 'Google' },
        { value: SOURCES.TIKTOK, label: 'TikTok' },
        ...(showInstagram
            ? [{ value: SOURCES.INSTAGRAM, label: 'Instagram' }]
            : []),
    ];
    const [activeSource, setActiveSource] = useState(SOURCES.TIKTOK);

    if (!selectedRow) {
        return <Message>Item not found.</Message>;
    }
    return (
        <Stack $space="1rem" css="padding-bottom: 1.5rem;">
            <DrawerHeader
                name={name}
                eyebrow={
                    <>
                        <DimensionIcon dimensionType={relatedType} />
                        {relatedType}
                    </>
                }
                contextLabel={parentEntity?.name}
                contextPrefix="in"
            >
                {showAddToWatchlist && (
                    <ToggleWatch
                        id={uuidv5(parentEntityId, selectedId)}
                        data={watchlistData}
                        btnChildren="Add to watchlist"
                    />
                )}
                <Button variant="default" size="sm" asChild>
                    <LinkTracked
                        to={relatedItemTo}
                        mixpanelContext="DataTableDrawer"
                        mixpanelMeta={{ itemNames: [name, parentEntity?.name] }}
                    >
                        See more data
                        <FiExternalLink />
                    </LinkTracked>
                </Button>
            </DrawerHeader>
            <PaddedStack
                $space=".5rem"
                $direction="row"
                css={`
                    display: flex;
                    flex-direction: row;
                `}
            >
                <MetricStat
                    metric={POPULARITY_METADATA_BY_KEY.YoY.format(
                        selectedRow?.[POPULARITY_METADATA_BY_KEY.YoY.key],
                    )}
                    metricLabel={
                        POPULARITY_METADATA_BY_KEY.YoY.tableHeadColumnLabel
                    }
                />
                <MetricStat
                    dataSource="GOOGLE"
                    metric={POPULARITY_METADATA_BY_KEY.search_YoY.format(
                        selectedRow?.[
                            POPULARITY_METADATA_BY_KEY.search_YoY.key
                        ],
                    )}
                    metricLabel={
                        POPULARITY_METADATA_BY_KEY.search_YoY
                            .tableHeadColumnLabel
                    }
                />
                <MetricStat
                    dataSource="TIKTOK"
                    metric={POPULARITY_METADATA_BY_KEY.tiktok_YoY.format(
                        selectedRow?.[
                            POPULARITY_METADATA_BY_KEY.tiktok_YoY.key
                        ],
                    )}
                    metricLabel={
                        POPULARITY_METADATA_BY_KEY.tiktok_YoY
                            .tableHeadColumnLabel
                    }
                />
                {showInstagram && (
                    <MetricStat
                        dataSource="INSTAGRAM"
                        metric={POPULARITY_METADATA_BY_KEY.instagram_YoY.format(
                            selectedRow?.[
                                POPULARITY_METADATA_BY_KEY.instagram_YoY.key
                            ],
                        )}
                        metricLabel={
                            POPULARITY_METADATA_BY_KEY.instagram_YoY
                                .tableHeadColumnLabel
                        }
                    />
                )}
            </PaddedStack>
            <PaddedStack>
                {relatedType === 'category' ? (
                    <CategoryDescription
                        categoryId={selectedId}
                        categoryName={name}
                    />
                ) : null}
            </PaddedStack>
            <Line />
            <PaddedStack>
                <div css="display: flex; justify-content: center;">
                    <PillTabs
                        ariaLabel="Data source"
                        options={sourceTabs}
                        value={activeSource}
                        onChange={setActiveSource}
                    />
                </div>
            </PaddedStack>
            <Stack $space="1.5rem">
                {activeSource === SOURCES.GOOGLE && (
                    <PaddedStack>
                        <TopRelatedSearchKeywords
                            countryId={countryId}
                            country={country}
                            name={name}
                            parentEntity={parentEntity}
                            parentEntityId={parentEntityId}
                            relatedType={relatedType}
                            selectedId={selectedId}
                        />
                    </PaddedStack>
                )}
                {activeSource === SOURCES.TIKTOK && (
                    <>
                        <PaddedStack>
                            <TopRelatedTikTokTerms
                                countryId={countryId}
                                country={country}
                                name={name}
                                parentEntity={parentEntity}
                                parentEntityId={parentEntityId}
                                relatedType={relatedType}
                                selectedId={selectedId}
                                week={week}
                                year={year}
                            />
                        </PaddedStack>
                        <Line />
                        <PaddedStack>
                            <TopPostsDrawerRelatedEntities
                                year={year}
                                week={week}
                                countryId={countryId}
                                parentEntity={parentEntity}
                                parentEntityId={parentEntityId}
                                relatedType={relatedType}
                                selectedId={selectedId}
                                displayQty={3}
                                name={name}
                            />
                        </PaddedStack>
                    </>
                )}
                {activeSource === SOURCES.INSTAGRAM && showInstagram && (
                    <>
                        <PaddedStack>
                            <TopRelatedInstagramTerms
                                countryId={countryId}
                                country={country}
                                parentEntity={parentEntity}
                                parentEntityId={parentEntityId}
                                relatedType={relatedType}
                                selectedId={selectedId}
                                name={name}
                            />
                        </PaddedStack>
                        <Line />
                        <PaddedStack>
                            <InstagramTopPostsDrawerRelatedAggregate
                                countryId={countryId}
                                parentEntity={parentEntity}
                                parentEntityId={parentEntityId}
                                relatedType={relatedType}
                                selectedId={selectedId}
                                displayQty={3}
                                name={name}
                            />
                        </PaddedStack>
                    </>
                )}
            </Stack>
        </Stack>
    );
}

export default PopularityIndexRelatedEntitiesDrawer;
